import NavBar from "../../components/NavBar/NavBar";
import Footer from "../../components/Footer/Footer";
import Button from "../../components/Button/Button";
import Checkmark from "../../components/Checkmark/Checkmark";
import { productDetails } from "../SignUpPage/productDetails";
import "./ProductPage.scss";
import part1Img from "../../assets/course-cards/frcophth-part-1.jpg";
import frcophth1QuestionsPage from "../../assets/products/frcophth1-questions-page.png";
import frcophth1ModulesPage from "../../assets/products/frcophth1-modules-page.png";
import frcophth1ModuleProgress from "../../assets/products/frcophth1-module-progress.png";
import frcophth1TimedExamPage from "../../assets/products/frcophth1-timed-exam-page.png";
import { FaCheckCircle, FaBook, FaStar, FaBrain, FaChartLine, FaBullseye } from "react-icons/fa";
import { topicIcons } from "./topicIcons";

interface FRCOphthPart1PageProps {
  product: {
    id: string;
    price: number;
    length: string;
    comingSoon: boolean;
  };
}

const FRCOphthPart1Page = ({ product }: FRCOphthPart1PageProps) => {
  const details = productDetails[product.id];

  return (
    <>
      <NavBar />
      <div id="ProductPage">
        {/* Hero Section */}
        <div className="product-hero-section">
          <div className="product-hero-background">
            <div className="product-hero-left"></div>
            <div 
              className="product-hero-image-bg"
              style={{
                backgroundImage: `url(${part1Img})`
              }}
            ></div>
          </div>

          <div className="product-hero-overlay">
            <div className="site-container">
              <div className="product-hero-content">
                <h1>FRCOphth Part 1 Question Bank</h1>
                <p className="product-description">
                  3,750+ exam-relevant MCQs. Includes 500+ high-yield exam-favourite questions. One focused goal: help you pass Part 1 FRCOphth.
                </p>
                <div className="product-pricing">
                  <span className="price">£{product.price}</span>
                  <span className="length">/ {product.length}</span>
                </div>
                {!product.comingSoon && (
                  <Button
                    text="Buy FRCOphth Part 1 Now"
                    linkTo={`/sign-up?product=${product.id}`}
                    type="navy"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="product-sales-content">
          <div className="site-container">
            
            {/* Introduction Section */}
            <section className="sales-section intro-section">
              <p className="lead-text">
                The FRCOphth Part 1 syllabus is vast, detailed, and heavily knowledge-based. Many candidates struggle not due to lack of effort — but because they revise inefficiently, without exposure to realistic exam-style questions.
              </p>
              <p className="solution-text">
                <strong>OphthalmologyQ provides the solution.</strong>
              </p>
            </section>

            {/* Questions Section */}
            <section className="sales-section questions-section">
              <div className="content-with-image">
                <div className="content-side">
                  <div className="section-header">
                    <FaBook className="section-icon" />
                    <h2>3,750+ High-Quality MCQs</h2>
                  </div>
                  <p className="lead-text">
                    Practise with 3,750+ carefully written multiple-choice questions, covering the full FRCOphth Part 1 curriculum.
                  </p>
                  <div className="topics-list">
                    <h3>Complete Syllabus Coverage:</h3>
                    <div className="topics-grid">
                      {details?.topicsCovered?.map((topic, index) => (
                        <div key={index} className="topic-card">
                          <div className="topic-icon">{topicIcons[topic]}</div>
                          <span className="topic-name">{topic}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="tagline-text">
                    Every topic tested. No gaps. No wasted time.
                  </p>
                </div>
                <div className="image-side">
                  <img src={frcophth1ModulesPage} alt="FRCOphth Part 1 Question Bank Modules" className="product-screenshot" />
                </div>
              </div>
            </section>

            {/* High-Yield Questions Section - Coming Soon (match Part 2 style) */}
            <section className="sales-section highlight-section">
              <div className="highlight-header">
                <div className="highlight-badge highlight-badge-coming-soon">
                  <FaStar className="star-icon" />
                  <span>Coming Soon</span>
                </div>
                <h2>500+ High-Yield Exam Questions (Coming Soon)</h2>
              </div>
              <p className="lead-text">
                A dedicated collection of 500+ high-yield MCQs focused on the topics that matter most for Part 1 success will be available soon:
              </p>
              <div className="highlight-features">
                <div className="highlight-feature-card">
                  <FaCheckCircle className="highlight-icon" />
                  <div>
                    <h3>Commonly examined topics</h3>
                    <p>Questions covering the most frequently tested areas</p>
                  </div>
                </div>
                <div className="highlight-feature-card">
                  <FaCheckCircle className="highlight-icon" />
                  <div>
                    <h3>Repeated exam themes</h3>
                    <p>Patterns and concepts that appear year after year</p>
                  </div>
                </div>
                <div className="highlight-feature-card">
                  <FaCheckCircle className="highlight-icon" />
                  <div>
                    <h3>High-impact differentiators</h3>
                    <p>The questions that separate successful candidates</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Explanations Section */}
            <section className="sales-section explanations-section">
              <div className="content-with-image reverse">
                <div className="image-side">
                  <img src={frcophth1QuestionsPage} alt="FRCOphth Part 1 Question with Explanation" className="product-screenshot" />
                </div>
                <div className="content-side">
                  <div className="section-header">
                    <FaBrain className="section-icon" />
                    <h2>Learn Faster with Clear Explanations</h2>
                  </div>
                  <p className="lead-text">
                    Every question comes with detailed explanations that help you truly understand the material. You'll see:
                  </p>
                  <div className="simple-list">
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>A clear explanation of the correct answer</p>
                    </div>
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>Reasons why the other options are incorrect</p>
                    </div>
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>Exam-relevant teaching points to reinforce learning</p>
                    </div>
                  </div>
                  <p className="emphasis-text">
                    You don't just practise — you understand.
                  </p>
                </div>
              </div>
            </section>

            {/* Analytics Section */}
            <section className="sales-section analytics-section">
              <div className="content-with-image">
                <div className="content-side">
                  <div className="section-header">
                    <FaChartLine className="section-icon" />
                    <h2>Track Progress. Target Weaknesses. Improve Faster.</h2>
                  </div>
                  <p className="lead-text">
                    Our built-in analytics help you monitor performance by topic, identify weak areas instantly, and focus revision where it matters most.
                  </p>
                  <p className="tagline-text">
                    Study smarter — not longer.
                  </p>
                </div>
                <div className="image-side">
                  <img src={frcophth1ModuleProgress} alt="FRCOphth Part 1 Module Progress Analytics" className="product-screenshot" />
                </div>
              </div>
            </section>

            {/* Exam Success Section */}
            <section className="sales-section exam-success-section">
              <div className="content-with-image reverse">
                <div className="image-side">
                  <img src={frcophth1TimedExamPage} alt="FRCOphth Part 1 Timed Exam Practice" className="product-screenshot" />
                </div>
                <div className="content-side">
                  <div className="section-header">
                    <FaBullseye className="section-icon" />
                    <h2>Built for Exam Day Success</h2>
                  </div>
                  <p className="lead-text">
                    Everything is designed to prepare you for how the exam actually feels — from exam-style MCQs at realistic difficulty, to time-pressured practice that builds confidence under exam conditions.
                  </p>
                  <p className="emphasis-text">
                    Walk into your exam with confidence.
                  </p>
                </div>
              </div>
            </section>

            {/* Pricing Section */}
            <section className="sales-section pricing-section">
              <div className="pricing-card">
                <h3>FRCOphth Part 1 Question Bank</h3>
                <div className="pricing-amount">
                  <span className="price-large">£{product.price}</span>
                  <span className="price-period">— 6 months full access</span>
                </div>
                <div className="pricing-includes">
                  <h4>Includes:</h4>
                  <ul>
                    <li><Checkmark /> 3,750+ MCQs</li>
                    <li><Checkmark /> 500+ high-yield exam questions</li>
                    <li><Checkmark /> Detailed explanations</li>
                    <li><Checkmark /> Performance analytics</li>
                  </ul>
                </div>
                <p className="pricing-note">
                  No hidden fees. No unnecessary extras.
                </p>
                <div className="pricing-cta">
                  <Button
                    text="Get Started Now"
                    linkTo={`/sign-up?product=${product.id}`}
                    type="navy"
                  />
                </div>
              </div>
            </section>

          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default FRCOphthPart1Page;

